import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { STATUS } from "../../lib/format";

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};

export const StatCard = ({ label, value, icon: Icon, tone = "purple", hint, testid }) => {
  const tones = {
    purple: "text-[#351080] bg-[#351080]/8",
    royal: "text-[#5720B8] bg-[#5720B8]/10",
    green: "text-[#078D67] bg-[#078D67]/10",
    orange: "text-[#D9760C] bg-[#F28A19]/12",
    navy: "text-[#17223B] bg-[#17223B]/8",
  };
  return (
    <div className="lp-card p-5 lp-fade" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] font-medium text-slate-500">{label}</p>
        {Icon && (
          <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}>
            <Icon className="h-[18px] w-[18px]" />
          </span>
        )}
      </div>
      <p className="mt-3 font-display text-3xl font-bold text-[#17223B] lp-num" data-testid={testid && `${testid}-value`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
};

export const PageHeader = ({ eyebrow, title, desc, actions }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      {eyebrow && <p className="lp-eyebrow mb-2">{eyebrow}</p>}
      <h1 className="font-display text-2xl font-bold text-[#17223B] sm:text-3xl" data-testid="page-title">{title}</h1>
      {desc && <p className="mt-1.5 max-w-2xl text-sm text-slate-500">{desc}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Pager = ({ page, pageSize, total, onPage, testid = "pager" }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-slate-500" data-testid={testid}>
      <span>
        {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} dari {total}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="lp-num">{page} / {pages}</span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const Empty = ({ text }) => <div className="py-10 text-center text-sm text-slate-400">{text}</div>;

export const CtaButton = ({ className = "", ...p }) => (
  <Button className={`bg-[#F28A19] text-white hover:bg-[#DC7A0F] transition-colors ${className}`} {...p} />
);
